import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import ChinaTrackerRejectModal from "./ChinaTrackerRejectModal";
import { useIsFocused } from "@react-navigation/native";
import {
  approveIncomingOrder,
  updateBulkIncomingOrder,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import {
  resetIncomingSearch,
  resetRejectedSearch,
  searchIncomingTrackerOrders,
  searchRejectedTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

// Define headers for the data table
const tableHeadersData = {
  MSRef: {
    name: "MS No.",
    width: 0.22,
    fontWeight: 700,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.22,
    fontWeight: 700,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.22,
  },
  ItemName: {
    name: "Item Name",
    width: 1,
  },
  StateDelivery: {
    name: "State",
    width: 0.16,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.22,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.22,
    fontWeight: 700,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.22,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.22,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.2,
    fontWeight: 700,
  },
  OrderStatus: {
    name: "Status",
    width: 0.25,
    fontWeight: 700,
  },
  Key: {
    name: "Key",
    width: 0.2,
    textAlign: "left",
    display: "hidden",
  },
  Checkbox: {
    name: "Checkbox",
    width: 0.4,
    display: "none",
  },
};

const ChinaTrackerIncomingOrders: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const listRef = useRef<ScrollView>(null);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);
  const { incomingOrders, rejectedOrders, loading, loadingIncomingAdd } =
    useSelector((state: any) => state.chinaTracker);
  const [orders, setOrders] = useState<any[]>([]);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("incoming"); // "incoming" or "pending"
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState<number>(0);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [, setContentOffSet] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose2 = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);

  // Date Filters
  const [dateFilter, setDateFilter] = useState<string>("all");

  const parseDate = (dateString: string) => {
    if (!dateString) return null;
    const trimmed = dateString.trim();
    const [day, month, year] = trimmed.split("/");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const normalizeStartOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

  const normalizeEndOfDay = (date: Date) =>
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    );

  const applyDateFilter = (data: any[]) => {
    if (dateFilter === "all") return data;

    const today = new Date();
    const nextMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    );

    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case "nextMonth":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        break;

      case "next3Months":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);
        break;

      case "next6Months":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 7, 0);
        break;

      case "upcomingMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
    }

    startDate = normalizeStartOfDay(startDate);
    endDate = normalizeEndOfDay(endDate);

    return data.filter((o) => {
      const date = parseDate(o.RequestDate);
      return date && date >= startDate && date <= endDate;
    });
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "accept") {
      const orders: any = [];
      for (const orderKey of order) {
        orders.push({
          key: orderKey.Key,
          orderNumber: orderKey.OrderNumber1,
          status: "APPROVED",
        });
      }
      const payload: any = {
        orders: orders,
      };
      dispatch(approveIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Incoming order approved successfully", {
            type: "success",
          });
          setActionRows([]);
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            {
              type: "danger",
            },
          );
        });
    } else if (action === "update_accept") {
      const orders: any = [];
      for (const orderKey of order) {
        orders.push({
          key: orderKey.Key,
          orderNumber: orderKey.OrderNumber1,
          status: "APPROVED",
        });
      }
      const payload: any = {
        orders: orders,
      };
      dispatch(updateBulkIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Incoming order updated as approved successfully", {
            type: "success",
          });
          setActionRows([]);
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            {
              type: "danger",
            },
          );
        });
    } else {
      setModalOpen(true);
      setSelectedItem(order[0]);
      setSelectedEnquiryType(action);
    }
  };
  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      if (activeTab === "incoming") {
        dispatch(searchIncomingTrackerOrders(searchedText));
      } else {
        dispatch(searchRejectedTrackerOrders(searchedText));
      }
    } else {
      if (activeTab === "incoming") {
        dispatch(resetIncomingSearch());
      } else {
        dispatch(resetRejectedSearch());
      }
    }
  };
  const isFocused = useIsFocused();

  // useEffect(() => {
  //   let isMounted: boolean = true;
  //   setRefresh(false);

  //   return () => {
  //     isMounted = false;
  //   };
  // }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  // useEffect(() => {
  //   setRefresh(false);
  //   if (activeTab === "pending") {
  //     setOrders(rejectedOrders);
  //     setTableHeaders(
  //       translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap)
  //     );
  //   } else {
  //     setOrders(incomingOrders);
  //     let headers = translateHeaders(
  //       tableHeadersData,
  //       t,
  //       chinaTrackerTranslationMap
  //     );
  //     const { Status, ...filteredHeaders } = headers;
  //     setTableHeaders(filteredHeaders);
  //   }
  // }, [activeTab, rejectedOrders, incomingOrders]);

  useEffect(() => {
    setRefresh(false);
    let filtered = activeTab === "pending" ? rejectedOrders : incomingOrders;
    filtered = applyDateFilter(filtered);
    setOrders(filtered);

    let headers = translateHeaders(
      tableHeadersData,
      t,
      chinaTrackerTranslationMap,
    );

    if (activeTab === "incoming") {
      const { Status, ...filteredHeaders } = headers;
      setTableHeaders(filteredHeaders);
    } else {
      setTableHeaders(headers);
    }
  }, [activeTab, rejectedOrders, incomingOrders, dateFilter]);

  return (
    <>
      {modalOpen &&
        ["reject", "update_reject"].includes(selectedEnquiryType) && (
          <ChinaTrackerRejectModal
            onClose={onClose2}
            visible={modalOpen}
            order={selectedItem}
            selectedEnquiryType={selectedEnquiryType}
          />
        )}
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: getInnerContainerWidth(), height: "100%" },
                ]}
              >
                <View
                  style={[
                    styles.titleContainer,
                    isSmallDevice && {
                      flexDirection: "column",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.titleText,
                      isSmallDevice && styles.titleTextSmall,
                    ]}
                  >
                    {t("incomingOrder")}
                  </Text>
                  {!isSmallDevice && (
                    <View style={[styles.searchContainer]}>
                      <LabelBannerWithSearchBar
                        titleName={``}
                        handleSearch={handleSearch}
                        setRefresh={setRefresh}
                        dataToExport={orders}
                        fileName="ItemTrackerScreen"
                        sheetName="Item Tracker Report Screen"
                      />
                    </View>
                  )}
                </View>

                <View style={styles.tabsContainer}>
                  <TouchableOpacity
                    style={[
                      styles.tab,
                      activeTab === "incoming" && styles.activeTab,
                    ]}
                    onPress={() => setActiveTab("incoming")}
                  >
                    <Text
                      style={[
                        styles.tabText,
                        activeTab === "incoming" && styles.activeTabText,
                      ]}
                    >
                      {t("incomingOrder")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.tab,
                      activeTab === "pending" && styles.activeTab,
                    ]}
                    onPress={() => setActiveTab("pending")}
                  >
                    <Text
                      style={[
                        styles.tabText,
                        activeTab === "pending" && styles.activeTabText,
                      ]}
                    >
                      {t("pending")}
                    </Text>
                  </TouchableOpacity>
                </View>

                <View style={[styles.filterChipsRow, { marginLeft: 15 }]}>
                  <ScrollView horizontal>
                    <TouchableOpacity onPress={() => setDateFilter("all")}>
                      <Text
                        style={
                          dateFilter === "all"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        All
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("upcomingMonth")}
                    >
                      <Text
                        style={
                          dateFilter === "upcomingMonth"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        Upcoming Month
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("nextMonth")}
                    >
                      <Text
                        style={
                          dateFilter === "nextMonth"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        Next Month
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("next3Months")}
                    >
                      <Text
                        style={
                          dateFilter === "next3Months"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        3 Months
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("next6Months")}
                    >
                      <Text
                        style={
                          dateFilter === "next6Months"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        6 Months
                      </Text>
                    </TouchableOpacity>
                  </ScrollView>
                </View>

                <View style={styles.actionButtonsContainer}>
                  <Text style={{ display: "flex", alignItems: "center" }}>
                    {actionRows?.length}/10{" "}
                    {translateFields(
                      "ItemsSelected",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <TouchableOpacity
                    style={
                      actionRows.length > 0
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "accept")
                        : navigateToOrder(actionRows, "update_accept");
                    }}
                    disabled={actionRows.length > 0 ? false : true}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields(
                        "Accept",
                        t,
                        chinaTrackerTranslationMap,
                      )}{" "}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={
                      actionRows.length === 1
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    disabled={actionRows.length === 1 ? false : true}
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "reject")
                        : navigateToOrder(actionRows, "update_reject");
                    }}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields(
                        "Reject",
                        t,
                        chinaTrackerTranslationMap,
                      )}{" "}
                    </Text>
                  </TouchableOpacity>
                </View>

                <ScrollView onScroll={handleScroll} ref={listRef}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{
                      minWidth: Math.max(screenWidth, 1200),
                    }}
                  >
                    <View
                      style={[
                        styles.containerContent,
                        { minWidth: Math.max(screenWidth, 1200) },
                      ]}
                    >
                      {loading || loadingIncomingAdd ? (
                        <View>
                          <Spinner
                            size="small"
                            color="#74BBFF"
                            styles={{ flexDirection: "row", margin: "20%" }}
                          />
                        </View>
                      ) : (
                        <View>
                          <ChinaTrackerDataTableWithPagination2
                            headers={tableHeaders}
                            rows={orders}
                            itemsPerPage={10}
                            isDataLoading={isSearched}
                            isDisplayTimeWithDate={true}
                            useModal={true}
                            isDetail={false}
                            isEdit={false}
                            isDelete={false}
                            actionRows={actionRows}
                            setActionRows={setActionRows}
                            itemTracker
                            // Required props
                            setModalVisible={() => {}}
                            handleRowClick={() => {}}
                            setSelected={() => {}}
                            handleDeleteRow={() => {}}
                            t={t}
                            page={page}
                            setPage={setPage}
                          />
                        </View>
                      )}
                    </View>
                  </ScrollView>
                </ScrollView>
                <View
                  style={[styles.footer, isSmallDevice && styles.footerSmall]}
                >
                  <TouchableOpacity
                    onPress={onClose}
                    style={[
                      styles.closeButton,
                      isSmallDevice && styles.buttonSmall,
                    ]}
                  >
                    <Text style={isSmallDevice && styles.buttonTextSmall}>
                      {t("close")}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%", height: "90%" },
              ]}
            >
              <View
                style={[
                  styles.titleContainer,
                  isSmallDevice && {
                    flexDirection: "column",
                  },
                ]}
              >
                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {t("incomingOrder")}
                </Text>
                {!isSmallDevice && (
                  <View style={[styles.searchContainer]}>
                    <LabelBannerWithSearchBar
                      titleName={``}
                      handleSearch={handleSearch}
                      setRefresh={setRefresh}
                      dataToExport={orders}
                      fileName="ItemTrackerScreen"
                      sheetName="Item Tracker Report Screen"
                    />
                  </View>
                )}
              </View>

              <View style={styles.tabsContainer}>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === "incoming" && styles.activeTab,
                  ]}
                  onPress={() => setActiveTab("incoming")}
                >
                  <Text
                    style={[
                      styles.tabText,
                      activeTab === "incoming" && styles.activeTabText,
                    ]}
                  >
                    {t("incomingOrder")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === "pending" && styles.activeTab,
                  ]}
                  onPress={() => setActiveTab("pending")}
                >
                  <Text
                    style={[
                      styles.tabText,
                      activeTab === "pending" && styles.activeTabText,
                    ]}
                  >
                    {t("pending")}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.actionButtonsContainer}>
                <TouchableOpacity
                  style={
                    actionRows.length > 0
                      ? styles.actionButton
                      : styles.disabledActionButton
                  }
                  onPress={() => {
                    activeTab === "incoming"
                      ? navigateToOrder(actionRows, "accept")
                      : navigateToOrder(actionRows, "update_accept");
                  }}
                  disabled={actionRows.length > 0 ? false : true}
                >
                  <Text style={styles.actionButtonText}>
                    {translateFields("Accept", t, chinaTrackerTranslationMap)}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={
                    actionRows.length > 0
                      ? styles.actionButton
                      : styles.disabledActionButton
                  }
                  disabled={actionRows.length > 0 ? false : true}
                  onPress={() => {
                    activeTab === "incoming"
                      ? navigateToOrder(actionRows, "reject")
                      : navigateToOrder(actionRows, "update_reject");
                  }}
                >
                  <Text style={styles.actionButtonText}>
                    {translateFields("Reject", t, chinaTrackerTranslationMap)}
                  </Text>
                </TouchableOpacity>
              </View>

              <ScrollView onScroll={handleScroll} ref={listRef}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{
                    minWidth: Math.max(screenWidth, 1200),
                  }}
                >
                  <View
                    style={[
                      styles.containerContent,
                      { minWidth: Math.max(screenWidth, 1200) },
                    ]}
                  >
                    {loading || loadingIncomingAdd ? (
                      <View>
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      </View>
                    ) : (
                      <View>
                        <ChinaTrackerDataTableWithPagination2
                          headers={tableHeaders}
                          rows={orders}
                          itemsPerPage={10}
                          isDataLoading={isSearched}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          actionRows={actionRows}
                          setActionRows={setActionRows}
                          itemTracker
                          // Required props
                          setModalVisible={() => {}}
                          handleRowClick={() => {}}
                          setSelected={() => {}}
                          handleDeleteRow={() => {}}
                          page={page}
                          setPage={setPage}
                        />
                      </View>
                    )}
                  </View>
                </ScrollView>
              </ScrollView>
              <View
                style={[styles.footer, isSmallDevice && styles.footerSmall]}
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={[
                    styles.closeButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text style={isSmallDevice && styles.buttonTextSmall}>
                    Close
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: 8,
    width: 200,
  },
  searchButton: {
    backgroundColor: "#f0f0f0",
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  tabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    marginBottom: 8,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#3498db",
  },
  tabText: {
    color: "#999",
  },
  activeTabText: {
    color: "#3498db",
    fontWeight: "600",
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: "#D8D8D8",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },

  tableScrollWrapper: {
    flex: 1,
    maxHeight: 520,
    width: "100%",
  },

  filterLabel: {
    fontSize: 10,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    marginHorizontal: 3,
  },

  selectedFilter: {
    fontSize: 10,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: "#56B7FD",
    borderRadius: 5,
    marginHorizontal: 3,
    backgroundColor: "#56B7FD",
    color: "#fff",
  },
});

export default ChinaTrackerIncomingOrders;
